import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINT } from '../../apiEndpoints';
import { FaUserSecret } from 'react-icons/fa';
import styles from './PromoCodeGate.module.css';

interface PromoCodeGateProps {
  onCodeValid: (code: string) => void;
}

type View = 'promo' | 'waitlist' | 'waitlist-success';

const PromoCodeGate: React.FC<PromoCodeGateProps> = ({ onCodeValid }) => {
  const [view, setView] = useState<View>('promo');
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistError, setWaitlistError] = useState('');

  const handlePromoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPromoError('');

    if (!promoCode.trim()) {
      setPromoError('Please enter an access code');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_ENDPOINT}/auth/validate-promo-code`, {
        code: promoCode.trim()
      });

      if (response.data.valid) {
        onCodeValid(promoCode.trim());
      } else {
        setPromoError('Hmm, that code didn\'t work. Double-check and try again.');
      }
    } catch {
      setPromoError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWaitlistError('');

    if (!waitlistEmail.trim()) {
      setWaitlistError('Email is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_ENDPOINT}/auth/waitlist`, {
        email: waitlistEmail.trim(),
        name: waitlistName.trim() || undefined
      });
      setView('waitlist-success');
    } catch (error: any) {
      setWaitlistError(
        error.response?.data?.error || 'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (view === 'waitlist-success') {
    return (
      <div className={styles.overlay}>
        <div className={styles.card}>
          <h2 className={styles.title}>You're on the list!</h2>
          <p className={styles.subtitle}>We'll be in touch soon.</p>
          <div className={styles.successMessage}>
            Thanks! We'll reach out when we're open to the public!
          </div>
          <div className={styles.links}>
            <p className={styles.linkText}>
              Already have an account?{' '}
              <Link to="/login" className={styles.link}>Sign in here</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'waitlist') {
    return (
      <div className={styles.overlay}>
        <div className={styles.card}>
          <div className={styles.icon}><FaUserSecret /></div>
          <h2 className={styles.title}>Join the Waitlist</h2>
          <p className={styles.subtitle}>We'll reach out when we're open to the public</p>

          <form onSubmit={handleWaitlistSubmit} className={styles.form}>
            {waitlistError && (
              <div className={styles.errorMessage}>{waitlistError}</div>
            )}
            <input
              type="text"
              value={waitlistName}
              onChange={(e) => setWaitlistName(e.target.value)}
              placeholder="Name (optional)"
              className={styles.input}
            />
            <input
              type="email"
              value={waitlistEmail}
              onChange={(e) => { setWaitlistEmail(e.target.value); setWaitlistError(''); }}
              placeholder="Email"
              className={waitlistError ? styles.inputError : styles.input}
            />
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Joining...' : 'Join the Waitlist'}
            </button>
          </form>

          <button
            className={styles.backLink}
            onClick={() => setView('promo')}
          >
            I have an access code
          </button>

          <div className={styles.links}>
            <p className={styles.linkText}>
              Already have an account?{' '}
              <Link to="/login" className={styles.link}>Sign in here</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.card}>
        <h2 className={styles.title}>Welcome to buSk</h2>
        <p className={styles.subtitle}>Enter your access code to get started</p>

        <form onSubmit={handlePromoSubmit} className={styles.form}>
          {promoError && (
            <div className={styles.errorMessage}>{promoError}</div>
          )}
          <input
            type="text"
            value={promoCode}
            onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
            placeholder="Access code"
            className={promoError ? styles.inputError : styles.input}
            autoFocus
          />
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Checking...' : 'Continue'}
          </button>
        </form>

        <div className={styles.divider}>or</div>

        <button
          className={styles.waitlistLink}
          onClick={() => setView('waitlist')}
        >
          No code? Join the waitlist
        </button>

        <div className={styles.links}>
          <p className={styles.linkText}>
            Already have an account?{' '}
            <Link to="/login" className={styles.link}>Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PromoCodeGate;
